import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, Settings2, Trash2, ChevronDown, ChevronUp, Plus, Edit2 } from "lucide-react";
import { toast } from "sonner";
import ReservationRulesForm from "@/components/reservations/ReservationRulesForm";

const DAY_NAMES = ["Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado", "Domingo"];

export default function ReservationRulesPage() {
  const { authAxios } = useAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [expanded, setExpanded] = useState({});

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get("/reservation-rules");
      setRules(res.data || []);
    } catch {
      toast.error("Erro ao carregar regras");
    }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const handleDelete = async (rule) => {
    if (!window.confirm(`Eliminar a regra "${rule.label || DAY_NAMES[rule.day_of_week]}"?`)) return;
    setDeletingId(rule.id);
    try {
      await authAxios.delete(`/reservation-rules/${rule.id}`);
      toast.success("Regra eliminada");
      fetchRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao eliminar");
    }
    setDeletingId(null);
  };

  const handleToggle = async (rule) => {
    setTogglingId(rule.id);
    try {
      await authAxios.patch(`/reservation-rules/${rule.id}`, { enabled: !rule.enabled });
      fetchRules();
    } catch {
      toast.error("Erro ao actualizar");
    }
    setTogglingId(null);
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setShowForm(true);
  };

  const handleCancelEdit = () => {
    setEditingRule(null);
    setShowForm(false);
  };

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings2 className="w-6 h-6 text-emerald-600" />
            Regras de Capacidade de Reservas
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Configure blocos de horário com capacidade máxima por dia da semana.
          </p>
        </div>
        <Button onClick={() => setShowForm(v => !v)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Plus className="w-4 h-4 mr-1.5" />
          Nova regra
        </Button>
      </div>

      {/* Form panel */}
      {showForm && (
        <div className="bg-white border rounded-xl p-5 mb-6 shadow-sm">
          <h2 className="text-base font-semibold mb-4 text-gray-800">
            {editingRule ? "Editar regra" : "Criar / substituir regra"}
          </h2>
          <ReservationRulesForm
            authAxios={authAxios}
            editingRule={editingRule}
            onSaved={() => { setShowForm(false); setEditingRule(null); fetchRules(); }}
            onCancel={handleCancelEdit}
          />
        </div>
      )}

      {/* Rules list */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
      ) : rules.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Settings2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">Nenhuma regra configurada ainda.</p>
          <p className="text-xs mt-1">Cria uma regra para definir a capacidade por bloco de horário.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map(rule => (
            <div key={rule.id} className={`bg-white border rounded-xl shadow-sm overflow-hidden transition ${!rule.enabled ? "opacity-60" : ""}`}>
              <div className="flex items-center gap-3 px-4 py-3">
                <Switch
                  checked={rule.enabled}
                  onCheckedChange={() => handleToggle(rule)}
                  disabled={togglingId === rule.id}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm truncate">
                    {rule.label || DAY_NAMES[rule.day_of_week]}
                  </p>
                  <p className="text-xs text-gray-400">
                    {DAY_NAMES[rule.day_of_week]} · {rule.slots?.length || 0} bloco{rule.slots?.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <button
                  onClick={() => toggleExpand(rule.id)}
                  className="p-1.5 text-gray-400 hover:text-gray-700 transition"
                >
                  {expanded[rule.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => handleEdit(rule)}
                  className="p-1.5 text-blue-400 hover:text-blue-600 transition"
                  title="Editar regra"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(rule)}
                  disabled={deletingId === rule.id}
                  className="p-1.5 text-red-400 hover:text-red-600 transition"
                  title="Eliminar regra"
                >
                  {deletingId === rule.id
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Trash2 className="w-4 h-4" />}
                </button>
              </div>

              {expanded[rule.id] && (
                <div className="border-t px-4 py-3 bg-gray-50">
                  <div className="grid grid-cols-3 gap-2 text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                    <span>Hora inicial</span><span>Hora final</span><span>Máx. pessoas</span>
                  </div>
                  {(rule.slots || []).map((slot, i) => (
                    <div key={i} className="grid grid-cols-3 gap-2 text-sm py-1.5 border-b last:border-0">
                      <span className="font-mono text-gray-700">{slot.start_time}</span>
                      <span className="font-mono text-gray-700">{slot.end_time}</span>
                      <span className="font-semibold text-emerald-700">{slot.max_people} pessoas</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
